
import React from "react";
import { StatCard, DonutWithLegend } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import { Maximize2, Minimize2, ShoppingCart, ShoppingBag, Users, Package, DollarSign, TrendingUp, History, FileText, Clock, Truck, CheckCircle, RotateCcw } from "lucide-react";
import { useInventoryStats } from "@/hooks/useGlobalStats";
import { STATUS_VARIANT_MAP } from "@/constants/globalConstants";

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6'];

export const InventoryStats = ({
  activeTab,
  viewMode = "inventory",
  subTab = "items",
  isExpanded,
  setIsExpanded,
  inventory = [],
  sales = [],
  returns = [],
  requests = [],
  purchaseReturns = [],
  purchases = [],
  openingStocks = []
}) => {
  const stats = useInventoryStats({
    inventory,
    sales,
    returns,
    requests,
    purchaseReturns,
    purchases,
    openingStocks,
    activeTab
  });

  const { stock, sales: salesStats, requests: requestStats, returns: returnStats } = stats;


  const renderStatsContent = () => {
    // 1. Returns View Mode
    if (viewMode === "returns") {
      const typeLabel = subTab === "master" ? "Master" : 
                        subTab === "purchases" ? "Purchases" : 
                        subTab === "site" ? "Site" : "Damaged";
      
      // For damaged tab, show all returns with damage reports regardless of status
      const typeFiltered = subTab === "master" ? returns :
                           subTab === "purchases" ? returns.filter(r => r.rawType === 'purchase' || r.returnType === 'Purchase') :
                           subTab === "site" ? returns.filter(r => r.rawType === 'site' || r.returnType === 'Site') :
                           returns.filter(r => {
                             if (r.rawType === 'damage' || r.returnType === 'Damage') return true;
                             if (r.originalData?.damageReport?.length > 0) return true;
                             if (r.originalData?.items?.some(it => it.damagedQty > 0)) return true;
                             if (r.items?.some(it => it.damagedQty > 0)) return true;
                             return false;
                           });

      // For damaged tab, don't filter by status — damaged items can be in any status
      const currentReturns = subTab === "damaged"
        ? typeFiltered
        : typeFiltered.filter(r => ['received', 'restocked'].includes((r.status || '').toLowerCase()));

      // For damaged tab: count only damaged qty & use damaged value, not total qty/amount
      const totalItemsCount = subTab === "damaged"
        ? currentReturns.reduce((sum, r) => {
            const orig = r.originalData || r;
            const damageReport = orig.damageReport || [];
            if (damageReport.length > 0) {
              return sum + damageReport.reduce((s, d) => s + (Number(d.damagedQty) || 0), 0);
            }
            const items = orig.items || r.items || [];
            return sum + items.reduce((s, it) => s + (Number(it.damagedQty) || 0), 0);
          }, 0)
        : currentReturns.reduce((sum, r) => sum + (r.items?.reduce((is, it) => is + (Number(it.quantity || it.qty || 0)), 0) || 0), 0);

      const totalAmountVal = subTab === "damaged"
        ? currentReturns.reduce((sum, r) => {
            const orig = r.originalData || r;
            const damageReport = orig.damageReport || [];
            const allItems = orig.items || r.items || [];

            if (damageReport.length > 0) {
              const drTotal = damageReport.reduce((s, d) => {
                const qty = Number(d.damagedQty) || 0;
                // rate on damageReport entry itself
                let rate = Number(d.rate || 0);
                // if no rate, look up from items by inventoryId / item id
                if (!rate) {
                  const targetId = (d.item?._id || d.item || d.inventoryId?._id || d.inventoryId || '').toString();
                  const matched = allItems.find(it => {
                    const itId = (it.inventoryId?._id || it.inventoryId || it.item?._id || it.item || '').toString();
                    return itId && itId === targetId;
                  }) || allItems[0];
                  rate = Number(matched?.rate || matched?.inventoryId?.purchaseRate || matched?.inventoryId?.unitPrice || matched?.item?.unitPrice || matched?.unitPrice || 0);
                }
                return s + (qty * rate);
              }, 0);
              // fall back to r.totalAmount if we still got 0 (e.g. rate not populated)
              return sum + (drTotal || Number(r.totalAmount || r.refundAmount || r.amount || 0));
            }

            // no damageReport — use damagedQty * rate from items
            const itemsTotal = allItems.reduce((s, it) => {
              const qty = Number(it.damagedQty) || 0;
              const rate = Number(it.rate || it.inventoryId?.purchaseRate || it.inventoryId?.unitPrice || it.unitPrice || 0);
              return s + (qty * rate);
            }, 0);
            return sum + (itemsTotal || Number(r.totalAmount || r.refundAmount || r.amount || 0));
          }, 0)
        : currentReturns.reduce((sum, r) => sum + (Number(r.totalAmount || r.refundAmount || r.amount || 0)), 0);

      return (
        <>
          <StatCard
            variant="dashboard"
            title="Total Returns"
            value={currentReturns.length}
            color="bg-brand-blue"
            isExpanded={false}
            icon={<RotateCcw />}
          />
          <StatCard
            variant="dashboard"
            title="Return Type"
            value={typeLabel}
            color="bg-brand-blue"
            isExpanded={false}
            icon={<History />}
          />
          <StatCard
            variant="dashboard"
            title="Return Items"
            value={totalItemsCount}
            color="bg-indigo-500"
            isExpanded={false}
            icon={<Package />}
          />
          <StatCard
            variant="dashboard"
            title="Total Amount"
            value={`₹${fmt(totalAmountVal)}`}
            color="bg-emerald-500"
            isExpanded={false}
            icon={<DollarSign />}
          />
        </>
      );
    }

    // 2. Requests View Mode
    if (viewMode === "requests") {
      return (
        <>
          <StatCard
            variant="dashboard"
            title="Total Requests"
            value={requestStats.total}
            color="bg-brand-blue"
            isExpanded={isExpanded}
            icon={<FileText />}
          />
          <StatCard
            variant="dashboard"
            title="Pending Approval"
            value={requestStats.pending}
            color="bg-amber-500"
            isExpanded={isExpanded}
            icon={<Clock />}
          />
          <StatCard
            variant="dashboard"
            title="In Transit"
            value={requestStats.moving}
            color="bg-indigo-500"
            isExpanded={isExpanded}
            icon={<Truck />}
          />
          <StatCard
            variant="dashboard"
            title="Delivered"
            value={requestStats.completed}
            color="bg-emerald-500"
            isExpanded={isExpanded}
            icon={<CheckCircle />}
          />
        </>
      );
    }

    // 3. Inventory Sub-Tabs
    switch (subTab) {
      case "purchases":
        const { purchases: pStats } = stats;
        return (
          <>
            <StatCard
              variant="dashboard"
              title="Total Spend"
              value={`₹${fmt(pStats.grossSpend || pStats.totalSpend)}`}
              color="bg-brand-blue"
              isExpanded={isExpanded}
              icon={<DollarSign />}
              tooltip={pStats.refundSpend > 0 ? `Net Retained: ₹${fmt(pStats.totalSpend)}\nRefunded: -₹${fmt(pStats.refundSpend)}` : undefined}
            >
              <DonutWithLegend data={stock.distribution} label="Supplier Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="dashboard"
              title="Items Purchased"
              value={pStats.grossQty || pStats.totalQty}
              color="bg-emerald-500"
              isExpanded={isExpanded}
              icon={<Package />}
              tooltip={pStats.returnedQty > 0 ? `Net Retained: ${pStats.totalQty} items\nReturned: -${pStats.returnedQty} items` : undefined}
            />
            <StatCard
              variant="dashboard"
              title="Transactions"
              value={pStats.transactions}
              color="bg-indigo-500"
              isExpanded={isExpanded}
              icon={<History />}
            />
            <StatCard
              variant="dashboard"
              title="Suppliers"
              value={pStats.suppliersCount}
              color="bg-amber-500"
              isExpanded={isExpanded}
              icon={<Users />}
            />
          </>
        );

      case "sales":
        return (
          <>
            <StatCard
              variant="dashboard"
              title="Total Revenue"
              value={`₹${fmt(salesStats.totalRevenue)}`}
              color="bg-emerald-500"
              isExpanded={isExpanded}
              icon={<TrendingUp />}
            >
              <DonutWithLegend data={stock.distribution} label="Vendor Distribution" labelColor="text-emerald-600" />
            </StatCard>
            <StatCard
              variant="dashboard"
              title="Items Sold"
              value={salesStats.totalQty}
              color="bg-brand-blue"
              isExpanded={isExpanded}
              icon={<ShoppingCart />}
            />
            <StatCard
              variant="dashboard"
              title="Orders"
              value={salesStats.count}
              color="bg-indigo-500"
              isExpanded={isExpanded}
              icon={<ShoppingBag />}
            />
            <StatCard
              variant="dashboard"
              title="Total Customers"
              value={salesStats.customersCount}
              color="bg-rose-500"
              isExpanded={isExpanded}
              icon={<Users />}
            />
          </>
        );

      case "open_stock":
        const { openStock } = stats;
        return (
          <>
            <StatCard
              variant="dashboard"
              title="Opening Value"
              value={`₹${fmt(openStock.totalAmount)}`}
              color="bg-brand-blue"
              isExpanded={isExpanded}
              icon={<DollarSign />}
            >
              <DonutWithLegend data={stock.distribution} label="Category Value Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="dashboard"
              title="Total Items Qty"
              value={openStock.totalUnits}
              color="bg-emerald-500"
              isExpanded={isExpanded}
              icon={<Package />}
            />
            <StatCard
              variant="dashboard"
              title="Stock Entries"
              value={openStock.entriesCount}
              color="bg-indigo-500"
              isExpanded={isExpanded}
              icon={<History />}
            />
            <StatCard
              variant="dashboard"
              title="Categories"
              value={openStock.categoriesCount}
              color="bg-amber-500"
              isExpanded={isExpanded}
              icon={<Users />}
            />
          </>
        );

      case "items":
      default:
        return (
          <>
            <StatCard
              variant="dashboard"
              title="Total Items"
              value={stock.totalItems}
              color="bg-brand-blue"
              isExpanded={isExpanded}
              icon={<Package />}
            >
              <DonutWithLegend data={stock.distribution} label="Stock Distribution" labelColor="text-blue-600" />
            </StatCard>
            <StatCard
              variant="dashboard"
              title="Inventory Value"
              value={`₹${fmt(stock.totalValue)}`}
              color="bg-emerald-500"
              isExpanded={isExpanded}
              icon={<DollarSign />}
            >
              <DonutWithLegend data={stock.distribution} label="Value Distribution" labelColor="text-emerald-600" />
            </StatCard>
            <StatCard
              variant="dashboard"
              title="Low Stock"
              value={stock.lowStockCount}
              color="bg-brand-blue"
              isExpanded={isExpanded}
              icon={<Clock />}
            >
              <div className="flex flex-col items-start justify-start h-full">
                <span className="text-[10px] font-bold text-brand-blue mt-2">Risk Heatmap</span>
                
              </div>
            </StatCard>
            <StatCard
              variant="dashboard"
              title="Out of Stock"
              value={stock.noStockCount}
              color="bg-rose-500"
              isExpanded={isExpanded}
              icon={<Package />}
            />
          </>
        );
    }
  };

  return (
    <div className="space-y-4 relative group/stats">
      {/* Expand/Collapse Toggle Icon */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="absolute -top-2 right-0 z-10 p-2 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-xl transition-all active:scale-95"
        title={isExpanded ? "Hide Charts" : "Show Charts"}
      >
        {isExpanded ? <Minimize2 size={10} /> : <Maximize2 size={10} />}
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr transition-all duration-300">
        {renderStatsContent()}
      </div>
    </div>
  );
};
